import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { registrationService } from '../../api/registrationService';
import toast from 'react-hot-toast';
import { 
  Building2, 
  Save,
  X
} from 'lucide-react';

const CreateTenantModal = ({ isOpen, onClose, onSuccess, tenant = null }) => {
  const [formData, setFormData] = useState({
    tenantId: null,
    tenantName: '',
    tenantCode: '',
    domain: '',
    status: 'active',
    subscriptionTier: 'standard',
    maxAgents: 10,
    maxTicketsPerMonth: 1000,
    storageLimitGb: 10
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditMode = Boolean(tenant?.tenantId);

  // Reset / prefill form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        setFormData({
          tenantId: tenant?.tenantId ?? null,
          tenantName: tenant?.tenantName || '',
          tenantCode: tenant?.tenantCode || '',
          domain: tenant?.domain || '',
          status: (tenant?.status || 'active').toLowerCase(),
          subscriptionTier: (tenant?.subscriptionTier || 'standard').toLowerCase(),
          maxAgents: tenant?.maxAgents ?? 10,
          maxTicketsPerMonth: tenant?.maxTicketsPerMonth ?? 1000,
          storageLimitGb: tenant?.storageLimitGb ?? 10
        });
      } else {
        setFormData({
          tenantId: null,
          tenantName: '',
          tenantCode: '',
          domain: '',
          status: 'active',
          subscriptionTier: 'standard',
          maxAgents: 10,
          maxTicketsPerMonth: 1000,
          storageLimitGb: 10
        });
      }
      setErrors({});
    }
  }, [isOpen, isEditMode, tenant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxAgents' || name === 'maxTicketsPerMonth' || name === 'storageLimitGb' 
        ? (value === '' ? '' : parseInt(value) || 0)
        : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tenantName.trim()) {
      newErrors.tenantName = 'Tenant name is required';
    } else if (formData.tenantName.length > 255) {
      newErrors.tenantName = 'Tenant name must not exceed 255 characters';
    }

    if (!formData.tenantCode.trim()) {
      newErrors.tenantCode = 'Tenant code is required';
    } else if (formData.tenantCode.length > 100) {
      newErrors.tenantCode = 'Tenant code must not exceed 100 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.tenantCode)) {
      newErrors.tenantCode = 'Tenant code must contain only alphanumeric characters, hyphens, and underscores';
    }

    if (formData.domain && formData.domain.length > 255) {
      newErrors.domain = 'Domain must not exceed 255 characters';
    }

    if (formData.maxAgents && formData.maxAgents < 1) {
      newErrors.maxAgents = 'Max agents must be at least 1';
    }

    if (formData.maxTicketsPerMonth && formData.maxTicketsPerMonth < 1) {
      newErrors.maxTicketsPerMonth = 'Max tickets per month must be at least 1';
    }

    if (formData.storageLimitGb && formData.storageLimitGb < 1) {
      newErrors.storageLimitGb = 'Storage limit must be at least 1 GB';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const payload = isEditMode
        ? { ...formData, tenantId: tenant?.tenantId ?? formData.tenantId ?? null }
        : { ...formData, tenantId: undefined };

      const response = isEditMode
        ? await registrationService.updateTenant(tenant.tenantId, payload)
        : await registrationService.createTenant(payload);
      
      // Handle response structure
      const responseData = response?.data;
      const tenantData = responseData?.data || responseData;
      
      if (tenantData) {
        toast.success(isEditMode ? 'Tenant updated successfully!' : 'Tenant created successfully!');
        if (onSuccess) {
          onSuccess(tenantData);
        }
        onClose();
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error(isEditMode ? 'Error updating tenant:' : 'Error creating tenant:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        (isEditMode ? 'Failed to update tenant' : 'Failed to create tenant');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={isEditMode ? 'Edit Tenant' : 'Create New Tenant'}
      size="lg"
    >
      <form onSubmit={handleSubmit} id="create-tenant-form" className="space-y-4">
        {/* Tenant Name */}
        <div className="space-y-2">
          <Label htmlFor="tenantName" className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-600" />
            Tenant Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="tenantName"
            name="tenantName"
            value={formData.tenantName}
            onChange={handleChange}
            placeholder="Enter tenant name (e.g., Acme Corporation)"
            className={errors.tenantName ? 'border-red-500' : ''}
            maxLength={255}
            disabled={saving}
            required
          />
          {errors.tenantName && (
            <p className="text-sm text-red-500">{errors.tenantName}</p>
          )}
        </div>

        {/* Tenant Code */}
        <div className="space-y-2">
          <Label htmlFor="tenantCode" className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-600" />
            Tenant Code <span className="text-red-500">*</span>
          </Label>
          <Input
            id="tenantCode"
            name="tenantCode"
            value={formData.tenantCode}
            onChange={handleChange}
            placeholder="Enter tenant code (e.g., acme_corp)"
            className={errors.tenantCode ? 'border-red-500' : ''}
            maxLength={100}
            disabled={saving}
            required
          />
          {errors.tenantCode && (
            <p className="text-sm text-red-500">{errors.tenantCode}</p>
          )}
          <p className="text-xs text-gray-500">Only alphanumeric characters, hyphens, and underscores allowed</p>
        </div>

        {/* Domain */}
        <div className="space-y-2">
          <Label htmlFor="domain">Domain (Optional)</Label>
          <Input
            id="domain"
            name="domain"
            value={formData.domain}
            onChange={handleChange}
            placeholder="Enter domain (e.g., acme.com)"
            className={errors.domain ? 'border-red-500' : ''}
            maxLength={255}
            disabled={saving}
          />
          {errors.domain && (
            <p className="text-sm text-red-500">{errors.domain}</p>
          )}
        </div>

        {/* Status and Subscription Tier Row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={saving}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subscriptionTier">Subscription Tier</Label>
            <select
              id="subscriptionTier"
              name="subscriptionTier"
              value={formData.subscriptionTier}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={saving}
            >
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>

        {/* Limits Row */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="maxAgents">Max Agents</Label>
            <Input
              id="maxAgents"
              name="maxAgents"
              type="number"
              min="1"
              value={formData.maxAgents}
              onChange={handleChange}
              className={errors.maxAgents ? 'border-red-500' : ''}
              disabled={saving}
            />
            {errors.maxAgents && (
              <p className="text-sm text-red-500">{errors.maxAgents}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxTicketsPerMonth">Max Tickets/Month</Label>
            <Input
              id="maxTicketsPerMonth"
              name="maxTicketsPerMonth"
              type="number"
              min="1"
              value={formData.maxTicketsPerMonth}
              onChange={handleChange}
              className={errors.maxTicketsPerMonth ? 'border-red-500' : ''}
              disabled={saving}
            />
            {errors.maxTicketsPerMonth && (
              <p className="text-sm text-red-500">{errors.maxTicketsPerMonth}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="storageLimitGb">Storage Limit (GB)</Label>
            <Input
              id="storageLimitGb"
              name="storageLimitGb"
              type="number"
              min="1"
              value={formData.storageLimitGb}
              onChange={handleChange}
              className={errors.storageLimitGb ? 'border-red-500' : ''}
              disabled={saving}
            />
            {errors.storageLimitGb && (
              <p className="text-sm text-red-500">{errors.storageLimitGb}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="px-6"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-tenant-form"
            disabled={saving}
            className="px-6 bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEditMode ? 'Update Tenant' : 'Create Tenant'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTenantModal;
